import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { NavLink, useNavigate, useParams } from "react-router";

import * as Api from "@/api";
import GoTo from "@/app/shared/GoTo";
import OverflowDrawer from "@/app/shared/OverflowDrawer";
import { NEW_GROUP } from "@/constants/New";
import Avatar from "@/design/components/Avatar";
import DeleteFlow from "@/design/components/DeleteFlow";
import HeaderCard from "@/design/components/HeaderCard";
import SaveIndicator from "@/design/components/SaveIndicator";
import FormGroup from "@/design/forms/FormGroup";
import InputAutocomplete from "@/design/forms/InputAutocomplete";
import InputNumber from "@/design/forms/InputNumber";
import InputNumeric from "@/design/forms/InputNumeric";
import InputSelect from "@/design/forms/InputSelect";
import InputText from "@/design/forms/InputText";
import InputToggle from "@/design/forms/InputToggle";
import StringForm from "@/design/forms/StringForm";
import { number, string } from "@/utils/hooks/endorse";
import useGoTo from "@/utils/hooks/useGoTo";
import useSave, { SaveState } from "@/utils/hooks/useSave";
import { toggleLoadingState } from "@/utils/utility";
import Valkyrie, {
  viArrowLeft,
  viClock,
  viGear,
  viLocationPinDot,
  viPeople,
  viPerson,
  viRepeat,
} from "@sippy-platform/valkyrie";

import { move } from "@dnd-kit/helpers";
import { DragDropProvider } from "@dnd-kit/react";
import { Alert, Box, Container, Divider, Grid, IconButton, List, ListItemText, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import MemberItem from "./_MemberItem";
import MemberItemMini from "./_MemberItemMini";
import groupReducer from "./groupReducer";
import GroupMembers from "./members/GroupMembers";

function Group() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up("md"));
  const { getGoTo } = useGoTo();

  const [tab, setTab] = useState(() => (id === "new" ? 1 : 0));
  const [previousId, setPreviousId] = useState(id);

  if (id !== previousId) {
    setPreviousId(id);
    setTab(id === "new" ? 1 : 0);
  }

  const pbx = useSelector((state) => state.domain.pbx);
  const musicOnHoldStore = useSelector((state) => state.domain.musicOnHolds);

  const handleTab = (event, newTab) => setTab(newTab);

  // Get general data
  useEffect(() => {
    Promise.all([Api.getMusicOnHolds(), Api.getUsers()]);
  }, []);

  const {
    value: group,
    dispatch,
    setSaveState,
    formState,
    errors,
    allErrors,
    save,
  } = useSave({
    initialValue: NEW_GROUP,
    reducer: groupReducer,
    validator: () => ({
      name: string()
        .required({ message: "Name is required." })
        .length([1, 40], { message: "Name must be at most 40 characters." }),
      exten: number()
        .required({ message: "Number is required." })
        .outside([100, 119], {
          message: "Number cannot be between 100 and 119.",
        })
        .not(0, { message: "Number cannot be 0." })
        .not(1711, { message: "Number cannot be 1711." })
        .not(1712, { message: "Number cannot be 1712." })
        .not(1722, { message: "Number cannot be 1722." })
        .not(1733, { message: "Number cannot be 1733." })
        .not(1813, { message: "Number cannot be 1813." }),
      ringSeconds: number()
        .required({
          condition: group.type === "RingallGroup",
          message: "Ring seconds is required.",
        })
        .min(1, {
          condition: group.type === "RingallGroup",
          message: "Ring seconds must be between 1 and 120 seconds.",
        })
        .max(120, {
          condition: group.type === "RingallGroup",
          message: "Ring seconds must be between 1 and 120 seconds.",
        }),
      repeat: number()
        .min(0, {
          condition: group.type === "LinearGroup",
          message: "Repeat must be between 0 and 10 times.",
        })
        .max(10, {
          condition: group.type === "LinearGroup",
          message: "Repeat must be between 0 and 10 times.",
        }),
    }),
    onSubmit: () => Api.storeGroup(group),
    onSuccess: (response) => {
      if (!group.id) {
        dispatch({ type: "id", value: response.data.group.id });
        navigate(`../${response.data.group.id}`);
      }

      Promise.resolve(Api.getGroups());
    },
  });

  const currentOverflow = useMemo(() => getGoTo(group.overflow), [getGoTo, group.overflow]);

  useEffect(() => {
    let ignore = false;

    if (Number(id) === group.id || !id) {
      return;
    }

    if (id === "new") {
      Api.cancelActiveGetRequests();
      dispatch({ type: "set", value: NEW_GROUP });

      return () => {
        ignore = true;
      };
    }

    toggleLoadingState(true);

    Promise.resolve(Api.getGroup({ id: Number(id) }))
      .then((response) => {
        if (!ignore) {
          setTab(0);
          dispatch({
            type: "set",
            value: {
              ...response.data.group,
              members: response.data.group.members.sort((a, b) => a.position - b.position),
            },
          });
        }
      })
      .finally(() => toggleLoadingState(false));

    return () => {
      ignore = true;
    };
  }, [dispatch, group.id, id]);

  const totalRingSeconds = useMemo(
    () => group.members.reduce((total, _member) => total + Number(_member.ringSeconds), 0),
    [group.members],
  );

  // Handle changes
  const formHandler = (property, value) => {
    dispatch({ type: property, value });
    setSaveState(SaveState.UNSAVED);
  };

  // Delete
  function onDeleteFinish() {
    Promise.resolve(Api.getGroups()).then(() => navigate(".."));
  }

  return (
    <Container>
      <title>{`${group.name || "Group"} \u00b7 ${pbx.name} \u00b7 ${import.meta.env.SIPPY_APP_TITLE}`}</title>
      <Stack
        sx={{
          gap: 2,
        }}
      >
        <HeaderCard
          tab={tab}
          changeTab={handleTab}
          tabs={[
            {
              label: "Overview",
              icon: viPeople,
              id: "overview",
              disabled: !group.id,
            },
            { label: "Settings", icon: viGear, id: "settings" },
            {
              label: "Members",
              icon: viPerson,
              id: "members",
              disabled: !group.id,
            },
          ]}
          avatar={
            !matches ? (
              <IconButton variant="avatar" component={NavLink} to={".."}>
                <Valkyrie icon={viArrowLeft} />
                <Avatar text={group.name} icon={viPeople} />
              </IconButton>
            ) : (
              <Avatar text={group.name} icon={viPeople} />
            )
          }
          action={
            <>
              <SaveIndicator formState={formState} errors={allErrors} save={save} />
              <DeleteFlow
                advanced
                key={group.id || "nk"}
                isActionable={!!group?.id}
                onDeleteCheck={() => Api.deleteGroup(group, false)}
                onDelete={() => Api.deleteGroup(group, true)}
                name={`${group.name} (${group.exten})`}
                onDeleteFinish={onDeleteFinish}
              />
            </>
          }
          title={group.name || "Unnamed group"}
          subheader={group.exten || "No number set"}
        />
        {group.members.length === 0 && group.id && (
          <Alert severity="error" sx={{ boxShadow: 1 }}>
            This group doesn't have any members. It won't function as expected.
          </Alert>
        )}
        {tab === 0 && (
          <>
            <FormGroup title="Settings">
              <Grid size={{ xs: 6, lg: 4 }}>
                <StringForm label="Music on Hold" text={group.musiconhold ?? "Ringing"} />
              </Grid>
              <Grid size={{ xs: 6, lg: 4 }}>
                <StringForm
                  label="Recording"
                  text={group.callRecording === "yes" ? "On" : group.callRecording === "no" ? "Off" : "Let user decide"}
                />
              </Grid>
              <Grid size={{ xs: 6, lg: 4 }}>
                <StringForm label="Type" text={group.type === "RingallGroup" ? "Ring all" : "Linear"} />
              </Grid>
              <Grid size={{ xs: 6, lg: 4 }}>
                {group.type === "RingallGroup" ? (
                  <StringForm label="Ring seconds" text={`${group.ringSeconds || ""} seconds`} />
                ) : (
                  <StringForm
                    label="Repeat"
                    text={`Repeats ${group.repeat === 1 ? "once" : `${group.repeat} times`}`}
                  />
                )}
              </Grid>
              <Grid size={{ xs: 12, sm: 6, lg: 8 }}>
                <StringForm
                  label="Overflow"
                  text={
                    currentOverflow ? (
                      <Stack
                        direction="row"
                        sx={{
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Avatar text={currentOverflow.name} px={24} icon={currentOverflow?.icon || viLocationPinDot} />
                        <ListItemText
                          primary={<GoTo target={currentOverflow} hideType />}
                          secondary={<GoTo target={currentOverflow} type />}
                          sx={{
                            ".MuiTypography-root": {
                              whiteSpace: "normal",
                              overflow: "visible",
                            },
                          }}
                        />
                      </Stack>
                    ) : (
                      "None"
                    )
                  }
                />
              </Grid>
            </FormGroup>
            <FormGroup title={`Members \u00b7 ${group.members.length}/50`}>
              <Grid size={12}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: `${group.type === "LinearGroup" ? "auto" : ""} auto 7fr 3fr ${group.callRecording === "undefined" ? "3fr" : ""} ${group.type === "LinearGroup" ? "2fr" : ""}`,
                    gap: 0.25,
                  }}
                >
                  <Box
                    sx={[
                      {
                        display: "grid",
                        gridTemplateColumns: "subgrid",
                        gridColumn: "1 / -1",
                        border: "none",
                        alignItems: "center",
                        gap: 1,
                        px: 1.75,
                        pb: 0.875,
                      },
                    ]}
                  >
                    {group.type === "LinearGroup" && (
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          textAlign: "end",
                        }}
                      >
                        #
                      </Typography>
                    )}
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        gridColumn: "span 2",
                      }}
                    >
                      User
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={[
                        {
                          fontWeight: 600,
                        },
                        group.type !== "LinearGroup" &&
                          group.callRecording !== "undefined" && {
                            textAlign: "end",
                          },
                      ]}
                    >
                      Number
                    </Typography>
                    {group.callRecording === "undefined" && (
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                        }}
                      >
                        Recording
                      </Typography>
                    )}
                    {group.type === "LinearGroup" && (
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          textAlign: "end",
                        }}
                      >
                        Ring seconds
                      </Typography>
                    )}
                  </Box>
                  {group.members.length >= 1 ? (
                    group.members.map((member) => <MemberItemMini key={member.id} member={member} group={group} />)
                  ) : (
                    <Box
                      sx={(theme) => ({
                        borderRadius: 1,
                        gridColumn: "1 / -1",
                        alignItems: "center",
                        px: 1.75,
                        py: 0.875,
                        backgroundColor: "#f8f8f8",
                        ...theme.applyStyles("dark", {
                          backgroundColor: "#28282d",
                        }),
                      })}
                    >
                      <Typography variant="body2">This group has no members...</Typography>
                    </Box>
                  )}
                </Box>
                {group.type === "LinearGroup" && (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <Stack
                      direction="row"
                      sx={{
                        justifyContent: "space-between",
                      }}
                    >
                      <Stack
                        direction="row"
                        sx={{
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Valkyrie icon={viRepeat} style={{ fontSize: 12 }} />
                        {group.repeat === 0 ? (
                          <Typography
                            sx={{
                              fontSize: "small",
                            }}
                          >
                            Does not repeat
                          </Typography>
                        ) : (
                          <Typography
                            sx={{
                              fontSize: "small",
                            }}
                          >
                            Repeats {group.repeat === 1 ? "once" : `${group.repeat} times`}
                          </Typography>
                        )}
                      </Stack>
                      <Stack
                        direction="row"
                        sx={{
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Valkyrie icon={viClock} style={{ fontSize: 12 }} />
                        <Typography
                          sx={{
                            fontSize: "small",
                          }}
                        >
                          {totalRingSeconds} ring seconds
                        </Typography>
                      </Stack>
                    </Stack>
                  </>
                )}
              </Grid>
            </FormGroup>
          </>
        )}
        {tab === 1 && (
          <>
            <FormGroup title="Identity">
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputText required id="name" label="Name" onChange={formHandler} value={group.name} error={errors} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputNumber
                  required
                  id="exten"
                  label="Number"
                  onChange={formHandler}
                  value={group.exten}
                  error={errors}
                  helperText="Number cannot be 0 or any Belgian emergency number."
                />
              </Grid>
            </FormGroup>
            <FormGroup title="Group type">
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputSelect
                  id="type"
                  label="Type"
                  onChange={formHandler}
                  value={group.type}
                  selectValue="value"
                  selects={[
                    { value: "RingallGroup", title: "Ring all" },
                    { value: "LinearGroup", title: "Linear" },
                  ]}
                  getOptionLabel={(option) => option.title}
                  error={errors}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                {group.type === "RingallGroup" ? (
                  <InputNumber
                    required
                    id="ringSeconds"
                    label="Ring seconds"
                    onChange={formHandler}
                    value={group.ringSeconds}
                    error={errors}
                    helperText="Maximum 120 seconds."
                  />
                ) : (
                  <InputNumeric
                    required
                    id="repeat"
                    label="Repeat"
                    min={0}
                    max={10}
                    onChange={formHandler}
                    value={group.repeat}
                    error={errors}
                    helperText="Number of times the list is repeated after the first time, a value between 0 and 10."
                  />
                )}
              </Grid>
            </FormGroup>
            <FormGroup title="Settings">
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputSelect
                  id="callRecording"
                  label="Recording"
                  onChange={formHandler}
                  value={group.callRecording}
                  selectValue="value"
                  selects={[
                    { value: "undefined", title: "Let user decide" },
                    { value: "no", title: "Off" },
                    { value: "yes", title: "On" },
                  ]}
                  getOptionLabel={(option) => option.title}
                  error={errors}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputAutocomplete
                  id="musiconhold"
                  label="Music on Hold"
                  onChange={formHandler}
                  value={group.musiconhold || "Ringing"}
                  selectValue="name"
                  selects={[...musicOnHoldStore.list, { name: "Ringing" }]}
                  getOptionLabel={(option) => option.name}
                  error={errors}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputToggle
                  id="answeredElsewhere"
                  onChange={formHandler}
                  states={[0, 1]}
                  value={group.answeredElsewhere}
                  primary="Disable missed call"
                  secondary="Don't show calls as unanswered if another member answered the call elsewhere on supported devices."
                  error={errors}
                />
              </Grid>
            </FormGroup>
            <FormGroup title="Overflow">
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <OverflowDrawer
                  label="Overflow"
                  value={group.overflow}
                  propertyName="overflow"
                  overflowHandler={(value) => formHandler("overflow", value)}
                  filter={{ exten: [group.exten], custom: [], sound: [] }}
                  helperText="Set the destination if the group does not pick up the call."
                />
              </Grid>
            </FormGroup>
          </>
        )}
        {tab === 2 && (
          <FormGroup
            title={`Members \u00b7 ${group.members.length}/50`}
            actions={<GroupMembers group={group} dispatch={dispatch} />}
          >
            <Grid size={12}>
              {group.type === "LinearGroup" ? (
                <>
                  <Stack
                    sx={{
                      gap: 0.25,
                    }}
                  >
                    {group.members.length >= 1 ? (
                      <DragDropProvider
                        onDragEnd={(event) => {
                          const members = move(group.members, event).map((member, index) => ({
                            ...member,
                            position: index + 1,
                          }));
                          const member = members.find((member) => member.id === event.operation.source.id);

                          dispatch({ type: "moveMember", event });

                          Promise.resolve(Api.putGroupMember(group, member));
                        }}
                      >
                        {group.members.map((member, key) => (
                          <MemberItem
                            key={member.id}
                            index={key}
                            id={member.id}
                            member={member}
                            group={group}
                            groupDispatch={dispatch}
                          />
                        ))}
                      </DragDropProvider>
                    ) : (
                      <Box
                        sx={(theme) => ({
                          borderRadius: 1,
                          gridColumn: "1 / -1",
                          alignItems: "center",
                          px: 1.75,
                          py: 1.5,
                          backgroundColor: "#f8f8f8",
                          ...theme.applyStyles("dark", {
                            backgroundColor: "#28282d",
                          }),
                        })}
                      >
                        <Typography variant="body2">This group has no members...</Typography>
                      </Box>
                    )}
                  </Stack>
                  <Divider sx={{ my: 1.5 }} />
                  <Stack
                    direction="row"
                    sx={{
                      justifyContent: "space-between",
                    }}
                  >
                    <Stack
                      direction="row"
                      sx={{
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Valkyrie icon={viRepeat} style={{ fontSize: 12 }} />
                      {group.repeat === 0 ? (
                        <Typography
                          sx={{
                            fontSize: "small",
                          }}
                        >
                          Does not repeat
                        </Typography>
                      ) : (
                        <Typography
                          sx={{
                            fontSize: "small",
                          }}
                        >
                          Repeats {group.repeat === 1 ? "once" : `${group.repeat} times`} after initial run.
                        </Typography>
                      )}
                    </Stack>
                    <Stack
                      direction="row"
                      sx={{
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Valkyrie icon={viClock} style={{ fontSize: 12 }} />
                      <Typography
                        sx={{
                          fontSize: "small",
                        }}
                      >
                        {totalRingSeconds} ring seconds
                      </Typography>
                    </Stack>
                  </Stack>
                </>
              ) : (
                <Stack
                  sx={{
                    gap: 0.25,
                  }}
                >
                  {group.members.length >= 1 ? (
                    <>
                      <List>
                        {group.members.map((member) => (
                          <MemberItem
                            key={member.id}
                            id={member.id}
                            member={member}
                            group={group}
                            groupDispatch={dispatch}
                          />
                        ))}
                      </List>
                    </>
                  ) : (
                    <Box
                      sx={(theme) => ({
                        borderRadius: 1,
                        gridColumn: "1 / -1",
                        alignItems: "center",
                        px: 1.75,
                        py: 1.5,
                        backgroundColor: "#f8f8f8",
                        ...theme.applyStyles("dark", {
                          backgroundColor: "#28282d",
                        }),
                      })}
                    >
                      <Typography variant="body2">This group has no members...</Typography>
                    </Box>
                  )}
                </Stack>
              )}
            </Grid>
          </FormGroup>
        )}
      </Stack>
    </Container>
  );
}

export default Group;
